
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, AlertTriangle, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/components/AuthProvider';
import { useAuthActions } from '@/hooks/subscription/useAuthActions';
import { useQueryClient } from '@tanstack/react-query';

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { user } = useAuth();
  const { handleSignOut, isLoading: isSigningOut } = useAuthActions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [detailedCheck, setDetailedCheck] = useState<any>(null);

  useEffect(() => {
    const validateInvitation = async () => {
      if (!token) {
        setError('Invalid invitation link. No token provided.');
        setLoading(false);
        return;
      }

      try {
        console.log('Validating invitation token: [token-redacted]');
        console.log('Token length:', token.length);
        
        // First try directly with the token as is
        const { data, error: inviteError } = await supabase
          .from('team_members')
          .select('*')
          .eq('invite_token', token.trim())
          .maybeSingle();
        
        if (data) {
          console.log('Found invitation matching token directly');
          setInvitation(data);
          setLoading(false);
          return;
        } else {
          console.log('Direct token match failed, trying alternative approach');
          
          // Try the raw SQL query as most reliable fallback
          const { data: rawMatches, error: rawError } = await supabase.rpc(
            'check_raw_token',
            { token_param: token.trim() }
          );
          
          if (rawMatches && Array.isArray(rawMatches) && rawMatches.length > 0) {
            console.log('Found invitation via raw SQL check');
            setInvitation(rawMatches[0]);
            setLoading(false);
            return;
          }
          
          // Try directly calling the edge function to validate the token
          if (user) {
            try {
              // Get the current session
              const { data: sessionData } = await supabase.auth.getSession();
              const accessToken = sessionData?.session?.access_token;
              
              if (!accessToken) {
                console.error('No access token available');
                throw new Error('Authentication required');
              }
              
              const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-invitation?token=${encodeURIComponent(token)}&userId=${user.id}`, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${accessToken}`
                }
              });
              
              if (response.ok) {
                const result = await response.json();
                if (result.success) {
                  console.log('Invitation validated via edge function');
                  navigate('/dashboard');
                  toast.success('You have successfully joined the team!');
                  return;
                }
              }
            } catch (edgeFuncError) {
              console.error('Error calling edge function directly:', edgeFuncError);
            }
          }
        }

        // For debugging in dev mode, collect more information
        if (import.meta.env.DEV) {
          // Check all pending invites
          const { data: allInvites, error: allInvitesError } = await supabase
            .from('team_members')
            .select('id, email, status, organization_id')
            .eq('status', 'pending');
          
          setDetailedCheck({
            allPendingInvites: allInvites || [],
            allPendingInvitesError: allInvitesError,
            pendingInvitesCount: allInvites?.length || 0,
          });
          
          // Store detailed debug info
          setDebugInfo({
            tokenLength: token.length,
            tokenTrimmedLength: token.trim().length,
          });
        }
        
        setError('This invitation is invalid or has expired.');
        setLoading(false);
        
      } catch (err: any) {
        console.error('Error validating invitation:', err);
        setError(`An error occurred while validating the invitation: ${err.message || 'Unknown error'}`);
        setLoading(false);
      }
    };

    validateInvitation();
  }, [token, user, navigate]);

  const acceptInvitation = async () => {
    if (!user || !invitation) return;
    
    setSubmitting(true);
    
    try {
      console.log('Accepting invitation for user:', user.id);
      console.log('Invitation data:', invitation);
      
      const organizationId = invitation.organization_id;
      console.log('Organization ID from invitation:', organizationId);
      
      // Get the current session for the authorization header
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      
      if (!accessToken) {
        throw new Error('No access token available. Please sign in again.');
      }
      
      // First, verify if the invitation is still valid and pending
      const { data: verifyInvite, error: verifyError } = await supabase
        .from('team_members')
        .select('id, status')
        .eq('id', invitation.id)
        .eq('status', 'pending')
        .single();
        
      if (verifyError || !verifyInvite) {
        throw new Error('This invitation is no longer valid. It may have been accepted already or revoked.');
      }
      
      // Call the handle-invitation edge function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          invitationId: invitation.id,
          userId: user.id,
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to accept invitation');
      }
      
      const result = await response.json();
      
      // If that fails, try the direct approach
      if (!result.success) {
        // Fallback to direct database update
        const { error: directUpdateError } = await supabase
          .from('team_members')
          .update({
            user_id: user.id,
            status: 'active',
            invite_token: null,
            updated_at: new Date().toISOString()
          })
          .eq('id', invitation.id)
          .eq('status', 'pending');
          
        if (directUpdateError) {
          console.error('Error with direct update:', directUpdateError);
          throw new Error('Failed to accept invitation through direct update');
        }
      }
      
      // Invalidate any cached queries to ensure fresh data is loaded
      queryClient.invalidateQueries({ queryKey: ['pixels', user.id] });
      queryClient.invalidateQueries({ queryKey: ['team-members', user.id] });
      
      toast.success('You have successfully joined the team!');
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      toast.error(`Failed to accept the invitation: ${err.message || 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUserSignOut = async () => {
    try {
      const currentUrl = window.location.pathname + window.location.search;
      
      await handleSignOut(() => {
        window.location.href = currentUrl;
      });
    } catch (error) {
      console.error('Error in sign out process:', error);
      toast.error('Failed to sign out. Please try again.');
    }
  };

  const handleUserSignUp = () => {
    const signupUrl = `/signup?redirect=/accept-invitation?token=${encodeURIComponent(token || '')}`;
    navigate(signupUrl);
  };

  const emailMismatch = user && invitation && user.email?.toLowerCase() !== invitation.email?.toLowerCase();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-600 mb-4" />
          <p className="text-gray-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (emailMismatch) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-xl">Email Mismatch</CardTitle>
            <CardDescription>
              This invitation was sent to <strong>{invitation.email}</strong>, but you are logged in as <strong>{user.email}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md mb-4">
              <p className="text-sm text-amber-700 flex items-start">
                <AlertTriangle className="h-4 w-4 mr-2 mt-0.5 shrink-0" />
                To accept this invitation, you need to sign in with the email address that the invitation was sent to.
              </p>
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={handleUserSignOut} 
              className="w-full" 
              variant="default"
              disabled={isSigningOut}
            >
              <LogOut className="mr-2 h-4 w-4" />
              {isSigningOut ? 'Signing out...' : 'Sign out and continue'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            {error ? (
              <>
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Invalid Invitation
              </>
            ) : (
              'Team Invitation'
            )}
          </CardTitle>
          <CardDescription>
            {error 
              ? error
              : `You've been invited to join as a team ${invitation?.role}.`}
          </CardDescription>
        </CardHeader>
        
        {!error && (
          <CardContent>
            {!user ? (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  You need to sign in or create an account to accept this invitation.
                </p>
                <p className="text-sm font-medium">
                  This invitation was sent to: <span className="text-black">{invitation?.email}</span>
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  You are logged in as: <strong>{user.email}</strong>
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  This invitation was sent to: <strong>{invitation?.email}</strong>
                </p>
              </>
            )}
          </CardContent>
        )}
        
        <CardFooter className="flex flex-col space-y-2">
          {error ? (
            <>
              <Button onClick={() => navigate('/login')} className="w-full">
                Go to Login
              </Button>
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline" 
                className="w-full"
              >
                Try Again
              </Button>
              {import.meta.env.DEV && debugInfo && (
                <div className="mt-4 p-4 bg-gray-100 rounded-md text-xs overflow-auto w-full max-h-80">
                  <p className="font-bold">Debug Info:</p>
                  <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
                  <p className="font-bold mt-2">Detailed Check:</p>
                  <pre>{JSON.stringify(detailedCheck, null, 2)}</pre>
                </div>
              )}
            </>
          ) : !user ? (
            <>
              <Button 
                onClick={() => navigate(`/login?redirect=/accept-invitation?token=${encodeURIComponent(token || '')}`)} 
                className="w-full"
              >
                Sign in to Accept
              </Button>
              <Button 
                onClick={() => navigate(`/signup?redirect=/accept-invitation?token=${encodeURIComponent(token || '')}`)} 
                variant="outline" 
                className="w-full"
              >
                Create an Account
              </Button>
            </>
          ) : (
            <Button 
              onClick={acceptInvitation} 
              className="w-full" 
              disabled={submitting}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {submitting ? 'Accepting...' : 'Accept Invitation'}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export default AcceptInvitation;
